import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';

import { AccountSelectors } from '@core/account-data';
import { filterDefined } from '@core/common-legacy';
import { FeatureSpecFacade } from '@core/feature-spec-logic';
import { FeatureCode } from '@core/feature-spec-types';
import { I18nService } from '@core/i18n-legacy';

import type { SupportedLanguage } from '../types';

@Injectable({
  providedIn: 'root',
})
export class LanguagesService {
  private readonly i18n = inject(I18nService);
  private readonly store = inject(Store);

  public readonly featureSpecFacade = inject(FeatureSpecFacade);
  public isMultilangEnabled: boolean;
  public companyLanguage: string;
  public languageSelectOptions: Array<{
    label: string;
    value: SupportedLanguage;
  }> = [];
  public languageNamesMap: Record<string, string> = this.getLanguageNamesMap();

  private supportedLanguages: SupportedLanguage[] = ['en', 'nl', 'de', 'fr', 'sv', 'fi'];

  private languageSelectOptionsKeys: Record<SupportedLanguage, string> = {
    en: 'app.common.languages.en',
    nl: 'app.common.languages.nl',
    de: 'app.common.languages.de',
    fr: 'app.common.languages.fr',
    sv: 'app.common.languages.sv',
    fi: 'app.common.languages.fi',
  };

  constructor() {
    this.languageSelectOptions = this.getLanguageSelectOptions();
    this.store
      .select(AccountSelectors.isMultilangEnabled)
      .subscribe(isMultilangEnabled => (this.isMultilangEnabled = isMultilangEnabled));

    this.store
      .select(AccountSelectors.currentCompany)
      .pipe(filterDefined)
      .subscribe(company => (this.companyLanguage = company.language));
  }

  private getLanguageSelectOptions(): Array<{
    label: string;
    value: SupportedLanguage;
  }> {
    return this.supportedLanguages.map(lang => ({
      label: this.i18n.translate(this.languageSelectOptionsKeys[lang]),
      value: lang,
    }));
  }

  private getLanguageNamesMap(): Record<SupportedLanguage, string> {
    return (
      this.featureSpecFacade
        .selectConfig(FeatureCode.Multilang)()
        ?.languages.reduce(
          (acc, lang) => {
            acc[lang.code] = lang.name;
            return acc;
          },
          <Record<string, string>>{},
        ) || {}
    );
  }
}
